import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';

import { previousQuestionId } from '../quiz/prune';
import type { Answer } from '../quiz/questions';
import {
  browserStorage,
  clearStoredSession,
  freshSession,
  loadOrCreateSession,
  normalizeOwnerKey,
  newSessionId,
  sessionReducer,
  writeStoredSession,
  type Clock,
  type IdFactory,
  type BrandProfile,
  type OnboardingSessionState,
} from './session';

export interface UseOnboardingSessionOptions {
  /** Injected in tests. */
  storage?: Storage | null;
  now?: Clock;
  newId?: IdFactory;
  /** The signed-in operator's identity (their email); a stored session of anyone else is discarded. */
  ownerKey?: string | null;
}

export interface OnboardingSessionApi {
  session: OnboardingSessionState;
  submitUrl: (url: string) => void;
  rejectUrl: () => void;
  /** Writes an answer (dropping the answers it makes obsolete) and ends any review: the chat moves on to the first unanswered question. */
  answer: (questionId: string, answer: Answer) => void;
  /**
   * The answered question the dock shows again after Back; null when the dock shows the first unanswered one.
   * UI only: never stored, so a reload lands on the first unanswered question.
   */
  reviewId: string | null;
  /** Show the question before the one on screen (prefilled). A no-op on the first question and once the quiz is done. */
  goBack: () => void;
  /** Saves the confirmed brand profile in the browser session (nothing is sent to the BFF). */
  confirmBrandProfile: (profile: Omit<BrandProfile, 'confirmedAt'>) => void;
  complete: () => void;
  /** Keep the site and the answers but use a new session id (the BFF says the old one belongs to another site). */
  rotateSessionId: () => void;
  /** Forget everything and start a new session id. */
  restart: () => void;
}

/**
 * The browser session: a reducer persisted to localStorage on every change. Resumes a stored session on
 * mount (same id, url, answers); anything stale or unreadable starts fresh.
 */
export function useOnboardingSession(options: UseOnboardingSessionOptions = {}): OnboardingSessionApi {
  const { now = Date.now, newId = newSessionId } = options;
  const ownerKey = normalizeOwnerKey(options.ownerKey);
  const storage = useMemo(
    () => (options.storage === undefined ? browserStorage() : options.storage),
    [options.storage],
  );
  const [session, dispatch] = useReducer(sessionReducer, undefined, () =>
    loadOrCreateSession(storage, now(), newId, ownerKey),
  );

  useEffect(() => {
    writeStoredSession(storage, session);
  }, [storage, session]);

  const submitUrl = useCallback((url: string) => dispatch({ type: 'urlSubmitted', url, now: now() }), [now]);
  const rejectUrl = useCallback(() => dispatch({ type: 'urlRejected', now: now() }), [now]);
  const [reviewId, setReviewId] = useState<string | null>(null);
  const answer = useCallback(
    (questionId: string, value: Answer) => {
      setReviewId(null);
      dispatch({ type: 'answered', questionId, answer: value, now: now() });
    },
    [now],
  );
  const { answers } = session;
  const goBack = useCallback(() => setReviewId((current) => previousQuestionId(answers, current)), [answers]);
  const confirmBrandProfile = useCallback(
    (profile: Omit<BrandProfile, 'confirmedAt'>) => dispatch({ type: 'brandProfileConfirmed', profile, now: now() }),
    [now],
  );
  const complete = useCallback(() => dispatch({ type: 'completed', now: now() }), [now]);
  const rotateSessionId = useCallback(
    () => dispatch({ type: 'sessionRotated', sessionId: newId(), now: now() }),
    [now, newId],
  );
  const restart = useCallback(() => {
    clearStoredSession(storage);
    setReviewId(null);
    dispatch({ type: 'reset', state: freshSession(now(), newId, ownerKey) });
  }, [storage, now, newId, ownerKey]);

  return { session, submitUrl, rejectUrl, answer, reviewId, goBack, confirmBrandProfile, complete, rotateSessionId, restart };
}
